import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { toast } from 'react-toastify';

// Types
interface ProfileData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  age?: number;
  gender?: 'male' | 'female' | 'other' | 'prefer-not-to-say';
  region?: string;
  province?: string;
  educationalStatus: 'studying' | 'not-studying' | 'graduated';
  educationalLevel?: 'primary' | 'secondary' | 'college' | 'university' | 'tvet' | 'form4-leaver';
  institutionName?: string;
  gradeLevel?: number;
  yearOfStudy?: number;
  graduationYear?: number;
  areasOfInterest: {
    mainCategory: string;
    subCategory: string;
    niche: string;
  }[];
  bio?: string;
  profilePicture?: string;
  competitionPreferences: {
    preferredStakeRange: 'low' | 'medium' | 'high' | 'any';
    preferredCategories: string[];
    timeZone: string;
  };
}

interface CompetitorProfileProps {
  isOpen: boolean;
  onClose: () => void;
}

const CompetitorProfile: React.FC<CompetitorProfileProps> = ({ isOpen, onClose }) => {
  const { user: authUser, token } = useAuth();
  
  const [profileData, setProfileData] = useState<ProfileData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    age: undefined,
    gender: 'prefer-not-to-say',
    region: '',
    province: '',
    educationalStatus: 'studying',
    educationalLevel: 'secondary',
    institutionName: '',
    gradeLevel: undefined,
    yearOfStudy: undefined,
    graduationYear: undefined,
    areasOfInterest: [],
    bio: '',
    profilePicture: '',
    competitionPreferences: {
      preferredStakeRange: 'medium',
      preferredCategories: [],
      timeZone: 'Africa/Nairobi'
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAddInterest, setShowAddInterest] = useState(false);
  const [newInterest, setNewInterest] = useState({
    mainCategory: '',
    subCategory: '',
    niche: ''
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Constants
  const CATEGORIES = [
    'Humanities', 'Sciences', 'Mathematics', 'Languages', 'Arts & Literature',
    'History & Geography', 'Technology & Computing', 'Business & Economics',
    'Sports & Physical Education', 'Social Sciences', 'Philosophy & Religion'
  ];

  const SUB_CATEGORIES = {
    'Humanities': ['Philosophy', 'Ethics', 'Logic', 'Critical Thinking'],
    'Sciences': ['Physics', 'Chemistry', 'Biology', 'Astronomy', 'Psychology'],
    'Mathematics': ['Algebra', 'Geometry', 'Calculus', 'Statistics', 'Number Theory'],
    'Languages': ['English', 'Swahili', 'French', 'German', 'Spanish'],
    'Arts & Literature': ['Literature', 'Poetry', 'Drama', 'Visual Arts', 'Music'],
    'History & Geography': ['World History', 'African History', 'Kenyan History'],
    'Technology & Computing': ['Programming', 'Web Development', 'AI', 'Cybersecurity'],
    'Business & Economics': ['Microeconomics', 'Macroeconomics', 'Business Management'],
    'Sports & Physical Education': ['Team Sports', 'Individual Sports', 'Fitness'],
    'Social Sciences': ['Sociology', 'Anthropology', 'Political Science'],
    'Philosophy & Religion': ['World Religions', 'Ethics', 'Metaphysics']
  };

  const NICHES = {
    'Philosophy': ['Ancient Philosophy', 'Modern Philosophy', 'African Philosophy'],
    'Physics': ['Classical Mechanics', 'Quantum Physics', 'Thermodynamics'],
    'Mathematics': ['Pure Mathematics', 'Applied Mathematics', 'Mathematical Logic'],
    'English': ['Grammar', 'Literature', 'Creative Writing', 'Linguistics'],
    'Literature': ['Classical Literature', 'Modern Literature', 'African Literature'],
    'World History': ['Ancient Civilizations', 'Medieval Period', 'Modern Era'],
    'Programming': ['Web Development', 'Mobile Development', 'Game Development'],
    'Economics': ['Development Economics', 'Behavioral Economics', 'International Trade']
  };

  const REGIONS = [
    'Nairobi', 'Mombasa', 'Kisumu', 'Nakuru', 'Eldoret', 'Thika', 'Nyeri'
  ];

  const PROVINCES = [
    'Nairobi', 'Coast', 'Eastern', 'Central', 'Rift Valley', 'Western', 'Nyanza'
  ];

  // Load profile data on component mount
  useEffect(() => {
    if (isOpen && authUser) {
      loadProfileData();
    }
  }, [isOpen, authUser]);

  const loadProfileData = async () => {
    if (!token || !authUser) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/users/profile/${authUser.id}`);
      
      if (response.data?.success) {
        const userProfile = response.data.data;
        setProfileData(prev => ({
          ...prev,
          firstName: userProfile.firstName || authUser.firstName || '',
          lastName: userProfile.lastName || authUser.lastName || '',
          email: userProfile.email || authUser.email || '',
          phone: userProfile.phone || '',
          age: userProfile.age,
          gender: userProfile.gender || 'prefer-not-to-say',
          region: userProfile.region || '',
          province: userProfile.province || '',
          educationalStatus: userProfile.educationalStatus || 'studying',
          educationalLevel: userProfile.educationalLevel || 'secondary',
          institutionName: userProfile.institutionName || '',
          gradeLevel: userProfile.gradeLevel,
          yearOfStudy: userProfile.yearOfStudy,
          graduationYear: userProfile.graduationYear,
          areasOfInterest: userProfile.areasOfInterest || [],
          bio: userProfile.bio || '',
          profilePicture: userProfile.profilePicture || '',
          competitionPreferences: userProfile.competitionPreferences || prev.competitionPreferences
        }));
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      // Initialize with auth user data if profile doesn't exist
      setProfileData(prev => ({
        ...prev,
        firstName: authUser.firstName || '',
        lastName: authUser.lastName || '',
        email: authUser.email || ''
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof ProfileData, value: any) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedChange = (parentField: keyof ProfileData, nestedField: string, value: any) => {
    setProfileData(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [nestedField]: value
      }
    }));
  };

  const handleProfilePictureUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setProfileData(prev => ({
          ...prev,
          profilePicture: result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const addAreaOfInterest = () => {
    if (newInterest.mainCategory && newInterest.subCategory && newInterest.niche) {
      setProfileData(prev => ({
        ...prev,
        areasOfInterest: [...prev.areasOfInterest, { ...newInterest }]
      }));
      setNewInterest({ mainCategory: '', subCategory: '', niche: '' });
      setShowAddInterest(false);
    }
  };

  const removeAreaOfInterest = (index: number) => {
    setProfileData(prev => ({
      ...prev,
      areasOfInterest: prev.areasOfInterest.filter((_, i) => i !== index)
    }));
  };

  const validateProfile = (): string | null => {
    if (!profileData.firstName.trim()) return 'First name is required';
    if (!profileData.lastName.trim()) return 'Last name is required';
    if (!profileData.email.trim()) return 'Email is required';
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(profileData.email)) return 'Please enter a valid email address';
    
    return null;
  };

  const saveProfile = async () => {
    if (!token || !authUser) return;
    
    // Ensure email is always populated from auth user if empty
    const dataToSave = {
      ...profileData,
      email: profileData.email || authUser.email || ''
    };
    
    // Validate profile data before saving
    const validationError = validateProfile();
    if (validationError) {
      toast.error(validationError);
      return;
    }
    
    try {
      setSaving(true);
      
      const response = await api.put(`/users/profile/${authUser.id}`, dataToSave);
      
      if (response.data?.success) {
        toast.success('Profile Saved Successfully! 🎉');
        // Auto-exit after showing toast
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        throw new Error('Failed to update profile');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getSubCategories = () => {
    return newInterest.mainCategory ? SUB_CATEGORIES[newInterest.mainCategory as keyof typeof SUB_CATEGORIES] || [] : [];
  };

  const getNiches = () => {
    return newInterest.subCategory ? NICHES[newInterest.subCategory as keyof typeof NICHES] || [] : [];
  };

  const calculateProfileCompletion = (): number => {
    let completedFields = 0;
    const totalFields = 12; // Total number of important fields
    
    if (profileData.firstName.trim()) completedFields++;
    if (profileData.lastName.trim()) completedFields++;
    if (profileData.email.trim()) completedFields++;
    if (profileData.phone?.trim()) completedFields++;
    if (profileData.age) completedFields++;
    if (profileData.gender && profileData.gender !== 'prefer-not-to-say') completedFields++;
    if (profileData.region?.trim()) completedFields++;
    if (profileData.province?.trim()) completedFields++;
    if (profileData.institutionName?.trim()) completedFields++;
    if (profileData.areasOfInterest.length > 0) completedFields++;
    if (profileData.bio?.trim()) completedFields++;
    if (profileData.profilePicture) completedFields++;
    
    return (completedFields / totalFields) * 100;
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={{
        background: 'white',
        borderRadius: '20px',
        width: '100%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '30px',
          borderRadius: '20px 20px 0 0',
          textAlign: 'center',
          position: 'relative'
        }}>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: '18px'
            }}
          >
            ×
          </button>
          <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: '700' }}>Competitor Profile</h1>
          <p style={{ margin: '10px 0 0 0', opacity: 0.9 }}>Customize your profile to enhance your competition experience</p>
        </div>
        
        <div style={{ padding: '30px' }}>

          {/* Profile Picture Section */}
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{ 
              fontSize: '1.5rem', 
              fontWeight: '600', 
              color: '#2f3542', 
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{
                width: '4px',
                height: '24px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '2px'
              }}></span>
              📸 Profile Picture
            </h2>
            <div style={{ textAlign: 'center' }}>
              <div style={{ position: 'relative', display: 'inline-block', marginBottom: '20px' }}>
                <div
                  style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    background: profileData.profilePicture 
                      ? `url(${profileData.profilePicture}) center/cover` 
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '3rem',
                    color: 'white',
                    margin: '0 auto 20px',
                    cursor: 'pointer',
                    border: '4px solid white',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {!profileData.profilePicture && '👤'}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePictureUpload}
                  style={{ display: 'none' }}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    background: 'linear-gradient(45deg, #4caf50, #8bc34a)',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '25px',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  {profileData.profilePicture ? 'Change Picture' : 'Upload Picture'}
                </button>
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{ 
              fontSize: '1.5rem', 
              fontWeight: '600', 
              color: '#2f3542', 
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{
                width: '4px',
                height: '24px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '2px'
              }}></span>
              👤 Personal Information
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <label style={{ fontWeight: '500', color: '#2f3542', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>
                  First Name *
                </label>
                <input
                  type="text"
                  value={profileData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  placeholder="Enter your first name"
                  style={{
                    padding: '12px 16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '10px',
                    fontSize: '1rem',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              <div>
                <label style={{ fontWeight: '500', color: '#2f3542', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>
                  Last Name *
                </label>
                <input
                  type="text"
                  value={profileData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  placeholder="Enter your last name"
                  style={{
                    padding: '12px 16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '10px',
                    fontSize: '1rem',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              <div>
                <label style={{ fontWeight: '500', color: '#2f3542', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>
                  Email *
                </label>
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Enter your email"
                  style={{
                    padding: '12px 16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '10px',
                    fontSize: '1rem',
                    width: '100%',
                    boxSizing: 'border-box',
                    background: 'white'
                  }}
                />
                <small style={{ 
                  color: '#666', 
                  fontSize: '0.8rem', 
                  marginTop: '4px', 
                  display: 'block' 
                }}>
                  You can now edit your email address
                </small>
              </div>
              
              <div>
                <label style={{ fontWeight: '500', color: '#2f3542', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={profileData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="Enter your phone number"
                  style={{
                    padding: '12px 16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '10px',
                    fontSize: '1rem',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              <div>
                <label style={{ fontWeight: '500', color: '#2f3542', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>
                  Age
                </label>
                <input
                  type="number"
                  min="13"
                  max="100"
                  value={profileData.age || ''}
                  onChange={(e) => handleInputChange('age', e.target.value ? parseInt(e.target.value) : undefined)}
                  placeholder="Enter your age"
                  style={{
                    padding: '12px 16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '10px',
                    fontSize: '1rem',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              <div>
                <label style={{ fontWeight: '500', color: '#2f3542', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>
                  Gender
                </label>
                <select
                  value={profileData.gender}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                  style={{
                    padding: '12px 16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '10px',
                    fontSize: '1rem',
                    width: '100%',
                    boxSizing: 'border-box',
                    background: 'white'
                  }}
                >
                  <option value="prefer-not-to-say">Prefer not to say</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label style={{ fontWeight: '500', color: '#2f3542', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>
                  Region/Province
                </label>
                <select
                  value={profileData.province}
                  onChange={(e) => handleInputChange('province', e.target.value)}
                  style={{
                    padding: '12px 16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '10px',
                    fontSize: '1rem',
                    width: '100%',
                    boxSizing: 'border-box',
                    background: 'white'
                  }}
                >
                  <option value="">Select Province</option>
                  {PROVINCES.map(province => (
                    <option key={province} value={province}>{province}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label style={{ fontWeight: '500', color: '#2f3542', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>
                  Specific Region
                </label>
                <select
                  value={profileData.region}
                  onChange={(e) => handleInputChange('region', e.target.value)}
                  style={{
                    padding: '12px 16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '10px',
                    fontSize: '1rem',
                    width: '100%',
                    boxSizing: 'border-box',
                    background: 'white'
                  }}
                >
                  <option value="">Select Region</option>
                  {REGIONS.map(region => (
                    <option key={region} value={region}>{region}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Educational Information */}
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{ 
              fontSize: '1.5rem', 
              fontWeight: '600', 
              color: '#2f3542', 
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{
                width: '4px',
                height: '24px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '2px'
              }}></span>
              🎓 Educational Information
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <label style={{ fontWeight: '500', color: '#2f3542', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>
                  Currently Studying?
                </label>
                <select
                  value={profileData.educationalStatus}
                  onChange={(e) => handleInputChange('educationalStatus', e.target.value)}
                  style={{
                    padding: '12px 16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '10px',
                    fontSize: '1rem',
                    width: '100%',
                    boxSizing: 'border-box',
                    background: 'white'
                  }}
                >
                  <option value="studying">Yes, currently studying</option>
                  <option value="not-studying">No, not currently studying</option>
                  <option value="graduated">Graduated</option>
                </select>
              </div>
              
              {profileData.educationalStatus === 'studying' && (
                <>
                  <div>
                    <label style={{ fontWeight: '500', color: '#2f3542', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>
                      Educational Level
                    </label>
                    <select
                      value={profileData.educationalLevel}
                      onChange={(e) => handleInputChange('educationalLevel', e.target.value)}
                      style={{
                        padding: '12px 16px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '10px',
                        fontSize: '1rem',
                        width: '100%',
                        boxSizing: 'border-box',
                        background: 'white'
                      }}
                    >
                      <option value="primary">Primary School</option>
                      <option value="secondary">Secondary School</option>
                      <option value="college">College</option>
                      <option value="university">University</option>
                      <option value="tvet">TVET</option>
                      <option value="form4-leaver">Form 4 Leaver</option>
                    </select>
                  </div>
                  
                  <div>
                    <label style={{ fontWeight: '500', color: '#2f3542', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>
                      Institution Name
                    </label>
                    <input
                      type="text"
                      value={profileData.institutionName}
                      onChange={(e) => handleInputChange('institutionName', e.target.value)}
                      placeholder="Enter institution name"
                      style={{
                        padding: '12px 16px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '10px',
                        fontSize: '1rem',
                        width: '100%',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  
                  {profileData.educationalLevel === 'primary' && (
                    <div>
                      <label style={{ fontWeight: '500', color: '#2f3542', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>
                        Grade Level
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="8"
                        value={profileData.gradeLevel || ''}
                        onChange={(e) => handleInputChange('gradeLevel', e.target.value ? parseInt(e.target.value) : undefined)}
                        placeholder="Enter grade level"
                        style={{
                          padding: '12px 16px',
                          border: '2px solid #e0e0e0',
                          borderRadius: '10px',
                          fontSize: '1rem',
                          width: '100%',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  )}
                  
                  {profileData.educationalLevel === 'secondary' && (
                    <div>
                      <label style={{ fontWeight: '500', color: '#2f3542', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>
                        Form/Year
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="4"
                        value={profileData.yearOfStudy || ''}
                        onChange={(e) => handleInputChange('yearOfStudy', e.target.value ? parseInt(e.target.value) : undefined)}
                        placeholder="Enter form/year"
                        style={{
                          padding: '12px 16px',
                          border: '2px solid #e0e0e0',
                          borderRadius: '10px',
                          fontSize: '1rem',
                          width: '100%',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  )}
                  
                  {['college', 'university', 'tvet'].includes(profileData.educationalLevel || '') && (
                    <div>
                      <label style={{ fontWeight: '500', color: '#2f3542', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>
                        Year of Study
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="8"
                        value={profileData.yearOfStudy || ''}
                        onChange={(e) => handleInputChange('yearOfStudy', e.target.value ? parseInt(e.target.value) : undefined)}
                        placeholder="Enter year of study"
                        style={{
                          padding: '12px 16px',
                          border: '2px solid #e0e0e0',
                          borderRadius: '10px',
                          fontSize: '1rem',
                          width: '100%',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  )}
                </>
              )}
              
              {profileData.educationalStatus === 'graduated' && (
                <div>
                  <label style={{ fontWeight: '500', color: '#2f3542', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>
                    Graduation Year
                  </label>
                  <input
                    type="number"
                    min="1950"
                    max={new Date().getFullYear()}
                    value={profileData.graduationYear || ''}
                    onChange={(e) => handleInputChange('graduationYear', e.target.value ? parseInt(e.target.value) : undefined)}
                    placeholder="Enter graduation year"
                    style={{
                      padding: '12px 16px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '10px',
                      fontSize: '1rem',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Areas of Interest */}
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{ 
              fontSize: '1.5rem', 
              fontWeight: '600', 
              color: '#2f3542', 
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{
                width: '4px',
                height: '24px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '2px'
              }}></span>
              🎯 Areas of Interest
            </h2>
            <div style={{ background: '#f8f9fa', borderRadius: '15px', padding: '25px' }}>
              {profileData.areasOfInterest.map((interest, index) => (
                <div key={index} style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '20px',
                  border: '1px solid #e0e0e0',
                  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#2f3542', margin: 0 }}>
                      {interest.mainCategory} → {interest.subCategory} → {interest.niche}
                    </h3>
                    <button
                      onClick={() => removeAreaOfInterest(index)}
                      style={{
                        background: '#ff4757',
                        color: 'white',
                        border: 'none',
                        width: '30px',
                        height: '30px',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        fontSize: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
              
              {!showAddInterest ? (
                <button
                  onClick={() => setShowAddInterest(true)}
                  style={{
                    background: 'linear-gradient(45deg, #667eea, #764ba2)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '25px',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    width: '100%'
                  }}
                >
                  + Add New Area of Interest
                </button>
              ) : (
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  border: '1px solid #e0e0e0',
                  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#2f3542', margin: 0 }}>Add New Interest</h3>
                    <button
                      onClick={() => setShowAddInterest(false)}
                      style={{
                        background: '#ff4757',
                        color: 'white',
                        border: 'none',
                        width: '30px',
                        height: '30px',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        fontSize: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      ×
                    </button>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div>
                      <label style={{ fontWeight: '500', color: '#2f3542', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>
                        Main Category
                      </label>
                      <select
                        value={newInterest.mainCategory}
                        onChange={(e) => setNewInterest(prev => ({ ...prev, mainCategory: e.target.value, subCategory: '', niche: '' }))}
                        style={{
                          padding: '12px 16px',
                          border: '2px solid #e0e0e0',
                          borderRadius: '10px',
                          fontSize: '1rem',
                          width: '100%',
                          boxSizing: 'border-box',
                          background: 'white'
                        }}
                      >
                        <option value="">Select Category</option>
                        {CATEGORIES.map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label style={{ fontWeight: '500', color: '#2f3542', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>
                        Sub Category
                      </label>
                      <select
                        value={newInterest.subCategory}
                        onChange={(e) => setNewInterest(prev => ({ ...prev, subCategory: e.target.value, niche: '' }))}
                        disabled={!newInterest.mainCategory}
                        style={{
                          padding: '12px 16px',
                          border: '2px solid #e0e0e0',
                          borderRadius: '10px',
                          fontSize: '1rem',
                          width: '100%',
                          boxSizing: 'border-box',
                          background: newInterest.mainCategory ? 'white' : '#f5f5f5'
                        }}
                      >
                        <option value="">Select Sub Category</option>
                        {getSubCategories().map(subCat => (
                          <option key={subCat} value={subCat}>{subCat}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label style={{ fontWeight: '500', color: '#2f3542', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>
                        Niche
                      </label>
                      <select
                        value={newInterest.niche}
                        onChange={(e) => setNewInterest(prev => ({ ...prev, niche: e.target.value }))}
                        disabled={!newInterest.subCategory}
                        style={{
                          padding: '12px 16px',
                          border: '2px solid #e0e0e0',
                          borderRadius: '10px',
                          fontSize: '1rem',
                          width: '100%',
                          boxSizing: 'border-box',
                          background: newInterest.subCategory ? 'white' : '#f5f5f5'
                        }}
                      >
                        <option value="">Select Niche</option>
                        {getNiches().map(niche => (
                          <option key={niche} value={niche}>{niche}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'center', marginTop: '20px' }}>
                    <button
                      onClick={addAreaOfInterest}
                      disabled={!newInterest.mainCategory || !newInterest.subCategory || !newInterest.niche}
                      style={{
                        background: 'linear-gradient(45deg, #4caf50, #8bc34a)',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '20px',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        marginRight: '10px',
                        opacity: (!newInterest.mainCategory || !newInterest.subCategory || !newInterest.niche) ? 0.6 : 1
                      }}
                    >
                      Add Interest
                    </button>
                    <button
                      onClick={() => setShowAddInterest(false)}
                      style={{
                        background: '#f1f2f6',
                        color: '#666',
                        border: '1px solid #ddd',
                        padding: '10px 20px',
                        borderRadius: '20px',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Bio */}
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{ 
              fontSize: '1.5rem', 
              fontWeight: '600', 
              color: '#2f3542', 
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{
                width: '4px',
                height: '24px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '2px'
              }}></span>
              📝 Bio
            </h2>
            <div>
              <label style={{ fontWeight: '500', color: '#2f3542', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>
                Tell us about yourself
              </label>
              <textarea
                value={profileData.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                placeholder="Share your interests, achievements, and what makes you unique as a competitor..."
                maxLength={500}
                style={{
                  padding: '12px 16px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '10px',
                  fontSize: '1rem',
                  width: '100%',
                  boxSizing: 'border-box',
                  minHeight: '100px',
                  resize: 'vertical'
                }}
              />
              <div style={{ textAlign: 'right', fontSize: '0.8rem', color: '#666', marginTop: '5px' }}>
                {profileData.bio?.length || 0}/500 characters
              </div>
            </div>
          </div>

          {/* Competition Preferences */}
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{ 
              fontSize: '1.5rem', 
              fontWeight: '600', 
              color: '#2f3542', 
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{
                width: '4px',
                height: '24px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '2px'
              }}></span>
              🏆 Competition Preferences
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <label style={{ fontWeight: '500', color: '#2f3542', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>
                  Preferred Stake Range
                </label>
                <select
                  value={profileData.competitionPreferences.preferredStakeRange}
                  onChange={(e) => handleNestedChange('competitionPreferences', 'preferredStakeRange', e.target.value)}
                  style={{
                    padding: '12px 16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '10px',
                    fontSize: '1rem',
                    width: '100%',
                    boxSizing: 'border-box',
                    background: 'white'
                  }}
                >
                  <option value="low">Low (KES 50-200)</option>
                  <option value="medium">Medium (KES 200-1000)</option>
                  <option value="high">High (KES 1000+)</option>
                  <option value="any">Any Range</option>
                </select>
              </div>
              
              <div>
                <label style={{ fontWeight: '500', color: '#2f3542', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>
                  Time Zone
                </label>
                <select
                  value={profileData.competitionPreferences.timeZone}
                  onChange={(e) => handleNestedChange('competitionPreferences', 'timeZone', e.target.value)}
                  style={{
                    padding: '12px 16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '10px',
                    fontSize: '1rem',
                    width: '100%',
                    boxSizing: 'border-box',
                    background: 'white'
                  }}
                >
                  <option value="Africa/Nairobi">East Africa Time (EAT)</option>
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="Europe/London">British Time (GMT)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Profile Completion Indicator */}
          <div style={{ 
            background: '#f8f9fa', 
            borderRadius: '15px', 
            padding: '20px', 
            marginBottom: '20px',
            border: '1px solid #e0e0e0'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '10px' 
            }}>
              <span style={{ fontWeight: '600', color: '#2f3542' }}>Profile Completion</span>
              <span style={{ fontWeight: '600', color: '#667eea' }}>
                {Math.round(calculateProfileCompletion())}%
              </span>
            </div>
            <div style={{
              width: '100%',
              height: '8px',
              background: '#e0e0e0',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${calculateProfileCompletion()}%`,
                height: '100%',
                background: 'linear-gradient(45deg, #667eea, #764ba2)',
                borderRadius: '4px',
                transition: 'width 0.5s ease'
              }} />
            </div>
            <small style={{ color: '#666', fontSize: '0.8rem', marginTop: '8px', display: 'block' }}>
              Complete your profile to increase your visibility to other competitors
            </small>
          </div>

          {/* Save Button */}
          <button
            onClick={saveProfile}
            disabled={saving}
            style={{
              background: 'linear-gradient(45deg, #4caf50, #8bc34a)',
              color: 'white',
              border: 'none',
              padding: '15px 40px',
              borderRadius: '25px',
              fontSize: '1.1rem',
              fontWeight: '600',
              cursor: saving ? 'not-allowed' : 'pointer',
              width: '100%',
              marginTop: '20px',
              opacity: saving ? 0.6 : 1,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {saving ? (
              <>
                <span style={{ animation: 'spin 1s linear infinite' }}>🔄</span>
                {' '}Saving Profile...
              </>
            ) : (
              '💾 Save Profile'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompetitorProfile;
